import { useState, useMemo, useEffect, useCallback } from "react";
import type {
  Job,
  Invoice,
  InvoiceType,
  ActivityEntry,
  PaymentDueAt,
  PaymentLineItem,
  PaymentLineItemKind,
  PaymentLineItemMethod,
  PaymentLineItemStatus,
  PaymentPlanType,
} from "@/data/types";
import {
  getFinancialSummary,
  evaluatePaymentGate,
  createAuditEntry,
  type DerivedPaymentStatus,
  type InvoiceAuditEntry,
} from "@/data/invoiceEngine";
import {
  deriveFinancialClearance,
  getClearanceLabel,
  getClearanceColor,
  getCustomerPaymentSummary,
} from "@/data/financialClearance";
import { getSettlementPolicy } from "@/data/settlementPolicy";
import { useAppData } from "@/data/AppDataContext";
import { SETTLEMENT_LABELS } from "@/data/installerWrapEngine";
import { JobService } from "@/services/job.service";
import { AccountingService, type PaymentMethod as DbPaymentMethod, type InvoiceRow, type PaymentRow } from "@/services/accounting.service";
import { isUuid, displayIdentity } from "@/lib/uuid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DollarSign, FileText, CreditCard, CheckCircle, Clock,
  AlertTriangle, XCircle, Send, ShieldAlert, Receipt,
  Landmark, Wallet, Info, Ban,
} from "lucide-react";
import { toast } from "sonner";

// ── Status display config ──

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  draft:   { label: "Draft", color: "bg-muted text-muted-foreground", icon: FileText },
  sent:    { label: "Sent", color: "bg-primary/10 text-primary", icon: Send },
  paid:    { label: "Paid", color: "bg-success/10 text-success", icon: CheckCircle },
  overdue: { label: "Overdue", color: "bg-destructive/10 text-destructive", icon: AlertTriangle },
};

const PAYMENT_STATUS_LABELS: Record<DerivedPaymentStatus, { label: string; color: string }> = {
  no_invoice_plan: { label: "No Invoices", color: "text-muted-foreground" },
  deposit_pending: { label: "Deposit Pending", color: "text-warning" },
  deposit_paid:    { label: "Deposit Paid", color: "text-primary" },
  progress_pending:{ label: "Progress Payment Due", color: "text-warning" },
  final_pending:   { label: "Final Payment Due", color: "text-warning" },
  paid_in_full:    { label: "Paid in Full", color: "text-success" },
  overdue:         { label: "Overdue", color: "text-destructive" },
};

function getFinancingStatusLabel(status: unknown): { label: string; color: string } {
  const normalized = String(status ?? "pending").toLowerCase();
  if (normalized === "approved") return { label: "Financing Approved", color: "text-success" };
  if (normalized === "declined") return { label: "Financing Rejected", color: "text-destructive" };
  return { label: "Financing Pending", color: "text-warning" };
}

const METHOD_ICONS: Record<string, React.ElementType> = {
  cash: Wallet,
  financed: Landmark,
  card: CreditCard,
  mixed: DollarSign,
};

const PAYMENT_MODE_LABELS: Record<PaymentPlanType, string> = {
  cash: "Cash / Check",
  financed: "Financing",
  card: "Card",
  mixed: "Combined",
};

const PAYMENT_DUE_LABELS: Record<PaymentDueAt, string> = {
  pre_schedule: "Before scheduling",
  pre_install: "Before install",
  post_install: "After install",
};

const FINANCING_PROVIDER_LABELS: Record<string, string> = {
  synchrony: "Synchrony",
  greensky: "GreenSky",
  other: "Other",
};

function formatMoney(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString();
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function todayInputDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | null {
  const date = value.trim();
  if (!date) return null;
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function isCreditAppliedToBalance(status: string): boolean {
  return ["Approved", "Offered to Customer", "Accepted", "Applied"].includes(status);
}

function normalizeInvoiceStatus(status: unknown): string {
  return String(status ?? "").trim().toLowerCase();
}

function getPaidInvoiceTotalsByType(invoices: Invoice[]): Map<InvoiceType, number> {
  const totals = new Map<InvoiceType, number>();
  invoices.forEach((invoice) => {
    if (normalizeInvoiceStatus(invoice.status) !== "paid") return;
    totals.set(invoice.type, roundCurrency((totals.get(invoice.type) ?? 0) + Number(invoice.amount || 0)));
  });
  return totals;
}

function reconcilePaymentLineStatuses(lineItems: PaymentLineItem[], invoices: Invoice[]): PaymentLineItem[] {
  const paidRemaining = getPaidInvoiceTotalsByType(invoices);

  return lineItems.map((line) => {
    if (line.status === "waived") return line;

    const amount = Number(line.amount || 0);
    const remainingForType = paidRemaining.get(line.kind) ?? 0;
    if (amount > 0 && remainingForType + 0.005 >= amount) {
      paidRemaining.set(line.kind, roundCurrency(remainingForType - amount));
      return line.status === "paid" ? line : { ...line, status: "paid" as const };
    }

    // If the line shows "paid" but no paid invoice covers it, revert.
    // Handles two cases: (a) invoice exists but is unpaid/voided-in-place,
    // (b) invoice was removed entirely after reverse+void.
    if (line.status === "paid") {
      return { ...line, status: line.method === "financed" ? "approved" as const : "pending" as const };
    }

    return line;
  });
}

function applyFinancingStatusToPaymentLines(
  lineItems: PaymentLineItem[],
  financingStatus: "pending" | "approved" | "declined",
): PaymentLineItem[] {
  return lineItems.map((line) => {
    if (line.method !== "financed" || line.status === "paid" || line.status === "waived") return line;
    return {
      ...line,
      status: financingStatus === "approved" ? "approved" as const : "pending" as const,
    };
  });
}

function mapDbInvoiceStatusToUi(status: string): Invoice["status"] {
  if (status === "issued" || status === "partially_paid") return "sent";
  if (status === "paid") return "paid";
  if (status === "overdue") return "overdue";
  return "draft";
}

function mapDbInvoiceTypeToUi(raw: string | null | undefined): Invoice["type"] {
  const value = String(raw || "").toLowerCase();
  if (value === "deposit" || value === "progress" || value === "final") return value;
  return "progress";
}

function toUiInvoiceFromDb(row: InvoiceRow, invoiceType: string | null | undefined): Invoice {
  const isFullyPaid = Number(row.balance_due ?? 0) <= 0;
  return {
    id: row.id,
    type: mapDbInvoiceTypeToUi(invoiceType),
    amount: Number(row.total ?? 0),
    status: isFullyPaid ? "paid" : mapDbInvoiceStatusToUi(row.status),
    issuedAt: row.issued_at ?? row.created_at,
    dueDate: row.due_date ?? undefined,
    paidAt: row.paid_at ?? undefined,
    paymentMethod: undefined,
    invoiceNumber: row.invoice_number ?? undefined,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim()) return error.trim();
  if (error && typeof error === "object") {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage.trim();
  }
  return fallback;
}

function isFinancialActivityEntry(entry: ActivityEntry): boolean {
  const text = `${entry.action ?? ""} ${entry.noteType ?? ""}`.toLowerCase();
  return (
    text.includes("invoice") ||
    text.includes("payment") ||
    text.includes("refund") ||
    text.includes("financ")
  );
}

function inferAuditAction(detail: string): InvoiceAuditEntry["action"] {
  const text = detail.toLowerCase();
  if (text.includes("refund")) return "refund_recorded";
  if (text.includes("void")) return "invoice_voided";
  if (text.includes("payment")) return "payment_recorded";
  if (text.includes("sent")) return "invoice_sent";
  if (text.includes("plan")) return "plan_generated";
  if (text.includes("overdue")) return "invoice_overdue";
  return "invoice_generated";
}

function toInvoiceAuditEntry(entry: ActivityEntry): InvoiceAuditEntry {
  return {
    id: entry.id,
    action: inferAuditAction(entry.action),
    timestamp: entry.date,
    detail: entry.action,
  };
}

function mergeInvoiceAuditEntries(...groups: InvoiceAuditEntry[][]): InvoiceAuditEntry[] {
  const merged: InvoiceAuditEntry[] = [];
  const seen = new Set<string>();

  for (const entry of groups.flat()) {
    const key = entry.id || `${entry.timestamp}|${entry.detail}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(entry);
  }

  return merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

function buildPaymentPlan(type: PaymentPlanType, netContractValue: number): NonNullable<Job["financials"]>["paymentPlan"] {
  const contractValue = Math.max(0, roundCurrency(netContractValue));
  const depositAmount = type === "financed" ? 0 : roundCurrency(contractValue * 0.5);
  const finalAmount = Math.max(0, roundCurrency(contractValue - depositAmount));
  const depositMethod = type === "card" ? "card" : "cash";
  const finalMethod = type === "financed" || type === "mixed" ? "financed" : depositMethod;

  return {
    type,
    requiresDeposit: depositAmount > 0,
    depositAmount,
    lineItems: [
      ...(depositAmount > 0
        ? [{
            id: crypto.randomUUID(),
            kind: "deposit" as const,
            method: depositMethod,
            amount: depositAmount,
            status: "pending" as const,
            dueAt: "pre_schedule" as const,
          }]
        : []),
      {
        id: crypto.randomUUID(),
        kind: "final",
        method: finalMethod,
        amount: finalAmount,
        status: type === "financed" ? "approved" : "pending",
        dueAt: type === "financed" || type === "mixed" ? "pre_install" : "post_install",
      },
    ],
  };
}

interface Props {
  job: Job;
  onJobUpdate: (updatedJob: Job) => void;
  canManage?: boolean;
}

export function JobFinancialsPanel({ job, onJobUpdate, canManage = false }: Props) {
  const [auditLog, setAuditLog] = useState<InvoiceAuditEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [contractValueModalOpen, setContractValueModalOpen] = useState(false);
  const [contractValueInput, setContractValueInput] = useState("");
  const [dbInvoices, setDbInvoices] = useState<Invoice[]>([]);
  const [dbInvoiceRows, setDbInvoiceRows] = useState<InvoiceRow[]>([]);
  const [dbPayments, setDbPayments] = useState<PaymentRow[]>([]);
  const { installerWrapRecords, credits } = useAppData();
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>("deposit");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [depositAmountMode, setDepositAmountMode] = useState<"fixed" | "percentage">("percentage");
  const [depositPercentage, setDepositPercentage] = useState("50");
  const [invoiceIssuedDate, setInvoiceIssuedDate] = useState(todayInputDate);
  const [invoiceDueDate, setInvoiceDueDate] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editInvoiceAmount, setEditInvoiceAmount] = useState("");
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<DbPaymentMethod>("credit_card");
  const [paymentReceivedDate, setPaymentReceivedDate] = useState(todayInputDate);
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [refundFormOpen, setRefundFormOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundSettlementDate, setRefundSettlementDate] = useState(todayInputDate);
  const [refundNotes, setRefundNotes] = useState("");
  const [structureLineOpen, setStructureLineOpen] = useState(false);
  const [editingStructureLineId, setEditingStructureLineId] = useState<string | null>(null);
  const [structureLineKind, setStructureLineKind] = useState<PaymentLineItemKind>("progress");
  const [structureLineMethod, setStructureLineMethod] = useState<PaymentLineItemMethod>("cash");
  const [structureLineStatus, setStructureLineStatus] = useState<PaymentLineItemStatus>("pending");
  const [structureLineDueAt, setStructureLineDueAt] = useState<PaymentDueAt>("pre_install");
  const [structureLineAmount, setStructureLineAmount] = useState("");
  const [structureLineAmountMode, setStructureLineAmountMode] = useState<"fixed" | "percentage">("fixed");
  const [structureLinePercentage, setStructureLinePercentage] = useState("");
  const [pendingPaymentMode, setPendingPaymentMode] = useState<PaymentPlanType | null>(null);
  const persistedAuditLog = useMemo(
    () => (job.activityHistory ?? [])
      .filter(isFinancialActivityEntry)
      .map(toInvoiceAuditEntry),
    [job.activityHistory],
  );
  const visibleAuditLog = useMemo(
    () => mergeInvoiceAuditEntries(persistedAuditLog, auditLog),
    [persistedAuditLog, auditLog],
  );

  const policy = useMemo(() => getSettlementPolicy(job.id, installerWrapRecords), [job.id, installerWrapRecords]);
  const invoices = job.financials?.invoices ?? [];
  const mergedInvoices = useMemo(() => {
    // Canonical source: DB invoices whenever available.
    if (dbInvoices.length > 0) return dbInvoices;
    return invoices;
  }, [invoices, dbInvoices]);
  const dbInvoiceById = useMemo(() => {
    const map = new Map<string, InvoiceRow>();
    dbInvoiceRows.forEach((row) => map.set(row.id, row));
    return map;
  }, [dbInvoiceRows]);
  const payments = job.financials?.payments ?? [];
  const paymentPlan = job.financials?.paymentPlan;
  const displayPaymentPlanLines = useMemo(
    () => reconcilePaymentLineStatuses(paymentPlan?.lineItems ?? [], mergedInvoices),
    [paymentPlan?.lineItems, mergedInvoices],
  );
  const statusJob = useMemo<Job>(() => ({
    ...job,
    financials: {
      ...job.financials,
      invoices: mergedInvoices,
      paymentPlan: paymentPlan
        ? { ...paymentPlan, lineItems: displayPaymentPlanLines }
        : paymentPlan,
    },
  }), [job, mergedInvoices, paymentPlan, displayPaymentPlanLines]);
  const summary = useMemo(() => getFinancialSummary(statusJob), [statusJob]);
  const gate = useMemo(() => evaluatePaymentGate(statusJob), [statusJob]);
  const clearance = useMemo(() => deriveFinancialClearance(statusJob), [statusJob]);
  const financing = job.financials?.financing;
  const currentPaymentMode = paymentPlan?.type
    ?? (financing?.financingStatus && financing.financingStatus !== "not_applicable" ? "financed" : undefined);
  const hasFinancedStructure = useMemo(
    () => (paymentPlan?.lineItems ?? []).some((line) => line.method === "financed") ||
      financing?.financingStatus === "approved" ||
      financing?.financingStatus === "pending" ||
      financing?.financingStatus === "declined",
    [paymentPlan?.lineItems, financing?.financingStatus],
  );
  const financedStructureTotal = useMemo(
    () => (paymentPlan?.lineItems ?? [])
      .filter((line) => line.method === "financed" && line.status !== "waived")
      .reduce((sum, line) => sum + Number(line.amount || 0), 0),
    [paymentPlan?.lineItems],
  );
  const jobCredits = useMemo(
    () => credits.filter((credit) => credit.jobId === job.id || credit.jobId === job.jobNumber),
    [credits, job.id, job.jobNumber],
  );
  const appliedCreditTotal = useMemo(
    () => jobCredits
      .filter((credit) => isCreditAppliedToBalance(credit.status))
      .reduce((sum, credit) => sum + Number(credit.creditAmount || 0), 0),
    [jobCredits],
  );
  const pendingCreditTotal = useMemo(
    () => jobCredits
      .filter((credit) => !isCreditAppliedToBalance(credit.status) && credit.status !== "Declined" && credit.status !== "Canceled")
      .reduce((sum, credit) => sum + Number(credit.creditAmount || 0), 0),
    [jobCredits],
  );
  const adjustedContractValue = useMemo(
    () => Math.max(0, Number(summary.contractValue || 0) - appliedCreditTotal),
    [summary.contractValue, appliedCreditTotal],
  );
  const computedDepositAmount = useMemo(() => {
    const pct = Number(depositPercentage);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return roundCurrency(adjustedContractValue * (pct / 100));
  }, [adjustedContractValue, depositPercentage]);
  const computedStructureLineAmount = useMemo(() => {
    const pct = Number(structureLinePercentage);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return roundCurrency(adjustedContractValue * (pct / 100));
  }, [adjustedContractValue, structureLinePercentage]);
  const localRefundTotal = useMemo(
    () => payments
      .filter((payment) => Number(payment.amount || 0) < 0)
      .reduce((sum, payment) => sum + Math.abs(Number(payment.amount || 0)), 0),
    [payments],
  );
  const dbTotals = useMemo(() => {
    if (dbInvoiceRows.length === 0 && dbPayments.length === 0) return null;
    const paidFromPayments = dbPayments.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const paidFromInvoiceRows = dbInvoiceRows.reduce((sum, row) => {
      const amountPaid = Number(row.amount_paid || 0);
      const total = Number(row.total || 0);
      const balanceDue = Number(row.balance_due || 0);
      const derivedPaid = Math.max(0, total - balanceDue);
      return sum + Math.max(amountPaid, derivedPaid);
    }, 0);
    const totalPaid = paidFromPayments > 0 ? paidFromPayments : paidFromInvoiceRows;
    return {
      totalPaid,
    };
  }, [dbInvoiceRows, dbPayments]);
  const fallbackTotals = useMemo(() => {
    const paidFromLocalPayments = payments
      .filter((payment) => Number(payment.amount || 0) > 0)
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const paidFromInvoices = mergedInvoices.reduce((sum, inv) => {
      const status = normalizeInvoiceStatus(inv.status);
      if (status !== "paid") return sum;
      return sum + Number(inv.amount || 0);
    }, 0);
    const totalPaid = paidFromLocalPayments > 0 ? paidFromLocalPayments : paidFromInvoices;
    return {
      totalPaid,
    };
  }, [payments, mergedInvoices]);
  const displayTotalPaid = useMemo(() => {
    const dbPaid = Number(dbTotals?.totalPaid ?? 0);
    const fallbackPaid = Number(fallbackTotals.totalPaid ?? 0);
    const summaryPaid = Number(summary.totalPaid ?? 0);
    return Math.max(0, Math.max(dbPaid, fallbackPaid, summaryPaid, 0) - localRefundTotal);
  }, [dbTotals?.totalPaid, fallbackTotals.totalPaid, summary.totalPaid, localRefundTotal]);
  const displayOutstandingBalance = useMemo(() => (
    Math.max(0, Number(adjustedContractValue || 0) - Number(displayTotalPaid || 0))
  ), [adjustedContractValue, displayTotalPaid]);
  const refundableBalance = useMemo(() => (
    Math.max(0, Number(displayTotalPaid || 0) - Number(adjustedContractValue || 0))
  ), [adjustedContractValue, displayTotalPaid]);
  const totalInvoiced = useMemo(
    () => mergedInvoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
    [mergedInvoices],
  );
  const paidPct = adjustedContractValue > 0 ? Math.min(100, Math.round((displayTotalPaid / adjustedContractValue) * 100)) : 0;
  const effectivePaymentStatus = useMemo<DerivedPaymentStatus>(() => {
    const contractValue = Math.max(0, Number(adjustedContractValue || 0));
    if (contractValue > 0 && Number(displayTotalPaid || 0) >= contractValue) {
      return "paid_in_full";
    }
    return summary.paymentStatus;
  }, [adjustedContractValue, summary.paymentStatus, displayTotalPaid]);
  const hasGeneratedInvoice = mergedInvoices.length > 0 || dbInvoiceRows.length > 0;
  const paymentStatusCfg = useMemo(() => {
    if (hasFinancedStructure && !hasGeneratedInvoice && effectivePaymentStatus === "no_invoice_plan") {
      return getFinancingStatusLabel(financing?.financingStatus);
    }
    return PAYMENT_STATUS_LABELS[effectivePaymentStatus];
  }, [effectivePaymentStatus, financing?.financingStatus, hasFinancedStructure, hasGeneratedInvoice]);

  const loadDbFinancialArtifacts = useCallback(async () => {
    const { data: invoiceRows, error } = await AccountingService.listInvoicesByJob(job.id);
    if (error) {
      console.error("[JobFinancialsPanel] Failed to load DB invoices for job:", error);
      return;
    }

    const activeInvoiceRows = invoiceRows.filter((row) => row.status !== "void");

    if (!activeInvoiceRows.length) {
      setDbInvoices([]);
      setDbInvoiceRows([]);
      setDbPayments([]);
      return;
    }
    setDbInvoiceRows(activeInvoiceRows);

    const invoiceIds = activeInvoiceRows.map((row) => row.id);
    const [invoiceItemsResult, paymentsResult] = await Promise.all([
      AccountingService.listInvoiceItems(invoiceIds),
      AccountingService.listPayments(invoiceIds),
    ]);
    const { data: invoiceItems, error: itemError } = invoiceItemsResult;
    if (itemError) {
      console.error("[JobFinancialsPanel] Failed to load DB invoice items for job:", itemError);
    }
    if (paymentsResult.error) {
      console.error("[JobFinancialsPanel] Failed to load DB payments for job:", paymentsResult.error);
    }
    setDbPayments(paymentsResult.data ?? []);

    const typeByInvoiceId = new Map<string, string>();
    invoiceItems.forEach((row) => {
      if (!typeByInvoiceId.has(row.invoice_id) && row.item_type) {
        typeByInvoiceId.set(row.invoice_id, row.item_type);
      }
    });

    const mapped = activeInvoiceRows.map((row) => toUiInvoiceFromDb(row, typeByInvoiceId.get(row.id)));
    setDbInvoices(mapped);
  }, [job.id]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await loadDbFinancialArtifacts();
    })();
    return () => {
      active = false;
    };
  }, [loadDbFinancialArtifacts]);

  const getOutstandingBalanceForInvoice = (invoice: Invoice): number => {
    const dbRow = dbInvoiceById.get(invoice.id);
    if (dbRow) return Math.max(0, Number(dbRow.balance_due || 0));
    return invoice.status === "paid" ? 0 : Math.max(0, Number(invoice.amount || 0));
  };

  const toActivityEntry = (entry: InvoiceAuditEntry): ActivityEntry => ({
    id: entry.id,
    date: entry.timestamp,
    action: entry.detail,
    user: "System",
    noteType: "system",
  });

  const updateJobFinancials = (patch: Partial<Job["financials"]>) => {
    const baseFinancials = job.financials ?? {
      contractValue: 0,
      estimatedMaterialCost: 0,
      estimatedLaborCost: 0,
      subcontractorPayout: 0,
      marginEstimate: 0,
      paymentStatus: "Not Invoiced",
      payoutStatus: "Not Submitted",
      invoices: [],
      payments: [],
    };
    const mergedFinancials = {
      ...baseFinancials,
      ...patch,
      paymentPlan: patch.paymentPlan ?? baseFinancials.paymentPlan,
      financing: patch.financing ?? baseFinancials.financing,
    };
    const updated = {
      ...job,
      financials: mergedFinancials,
    };
    onJobUpdate(updated);
    return updated.financials;
  };

  const handlePaymentModeChange = async (mode: PaymentPlanType) => {
    const previousJob = job;
    const nextPaymentPlan = buildPaymentPlan(mode, adjustedContractValue);
    const nextFinancing = mode === "financed" || mode === "mixed"
      ? {
          ...(job.financials?.financing ?? {}),
          financingStatus: job.financials?.financing?.financingStatus ?? "pending",
          financingApprovedAmount: job.financials?.financing?.financingApprovedAmount,
          customerDownPaymentAmount: mode === "mixed" ? nextPaymentPlan.depositAmount : job.financials?.financing?.customerDownPaymentAmount,
        }
      : { financingStatus: "not_applicable" as const };
    const nextFinancials = updateJobFinancials({
      paymentPlan: nextPaymentPlan,
      financing: nextFinancing,
    });
    const newAuditEntry = createAuditEntry(
      "plan_generated",
      `Payment mode set to ${PAYMENT_MODE_LABELS[mode]}`,
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;
    setAuditLog((prev) => [...prev, newAuditEntry]);
    toast.success(`Payment mode set to ${PAYMENT_MODE_LABELS[mode]}`);
  };

  const handleFinancingDetailsChange = async (
    patch: {
      financingStatus?: "pending" | "approved" | "declined";
      financingProvider?: string;
    },
  ) => {
    const previousJob = job;
    const currentFinancingStatus = financing?.financingStatus && financing.financingStatus !== "not_applicable"
      ? financing.financingStatus
      : "pending";
    const nextStatus = patch.financingStatus ?? currentFinancingStatus;
    const provider = patch.financingProvider ?? financing?.financingProvider;
    const normalizedProvider = provider && provider !== "unassigned" ? provider : undefined;
    const nextPaymentPlan = paymentPlan
      ? {
          ...paymentPlan,
          lineItems: applyFinancingStatusToPaymentLines(paymentPlan.lineItems, nextStatus),
        }
      : undefined;
    const nextFinancing = {
      ...(financing ?? {}),
      financingStatus: nextStatus,
      financingProvider: normalizedProvider,
      financingApprovedAmount: nextStatus === "approved"
        ? roundCurrency(financing?.financingApprovedAmount ?? financedStructureTotal)
        : financing?.financingApprovedAmount,
      customerDownPaymentAmount: financing?.customerDownPaymentAmount,
      financingTerm: financing?.financingTerm,
    };
    const nextFinancials = updateJobFinancials({
      ...(nextPaymentPlan ? { paymentPlan: nextPaymentPlan } : {}),
      financing: nextFinancing,
    });
    const providerLabel = normalizedProvider ? FINANCING_PROVIDER_LABELS[normalizedProvider] ?? normalizedProvider : "No provider selected";
    const newAuditEntry = createAuditEntry(
      "plan_generated",
      `Financing ${nextStatus}${normalizedProvider ? ` via ${providerLabel}` : ""}`,
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;
    setAuditLog((prev) => [...prev, newAuditEntry]);

    // Auto-create + send invoices for every financed line that just became "approved".
    if (nextStatus === "approved" && nextPaymentPlan) {
      const approvedLines = nextPaymentPlan.lineItems.filter(
        (li) => li.method === "financed" && li.status === "approved",
      );
      if (approvedLines.length > 0) {
        let updatedInvoices = [...mergedInvoices];
        const autoAuditEntries: InvoiceAuditEntry[] = [];

        for (const li of approvedLines) {
          // Skip if a non-paid invoice for this type already exists.
          const existing = updatedInvoices.find((inv) => inv.type === li.kind && inv.status !== "paid");
          let invoiceId: string;
          let invoiceAmount: number;

          if (existing && isUuid(existing.id)) {
            invoiceId = existing.id;
            invoiceAmount = existing.amount;
          } else {
            setSaving(true);
            const { invoice: created, error: createError } = await AccountingService.createInvoiceForJob({
              jobId: job.id,
              type: li.kind,
              amount: li.amount,
              status: "draft",
              issuedAt: new Date().toISOString(),
              description: `${li.kind[0].toUpperCase()}${li.kind.slice(1)} payment`,
            });
            setSaving(false);
            if (createError || !created) {
              toast.error(getErrorMessage(createError, "Failed to create invoice"));
              continue;
            }
            invoiceId = created.id;
            invoiceAmount = li.amount;
            updatedInvoices = [
              ...updatedInvoices,
              { id: invoiceId, type: li.kind, amount: invoiceAmount, status: "sent" as const, issuedAt: new Date().toISOString() },
            ];
            autoAuditEntries.push(
              createAuditEntry("invoice_created", `${li.kind} invoice auto-created: ${formatMoney(invoiceAmount)}`, { invoiceId }),
            );
          }

          setSaving(true);
          const { error: sendError } = await AccountingService.updateInvoiceStatus(invoiceId, "issued");
          setSaving(false);
          if (!sendError) {
            updatedInvoices = updatedInvoices.map((inv) =>
              inv.id === invoiceId ? { ...inv, status: "sent" as const } : inv,
            );
            autoAuditEntries.push(
              createAuditEntry("invoice_sent", `${li.kind} invoice auto-sent: ${formatMoney(invoiceAmount)}`, { invoiceId }),
            );
          }
        }

        if (autoAuditEntries.length > 0) {
          const nextPlanWithInvoices = {
            ...nextPaymentPlan,
            lineItems: reconcilePaymentLineStatuses(nextPaymentPlan.lineItems, updatedInvoices),
          };
          const invoiceFinancials = updateJobFinancials({
            invoices: updatedInvoices,
            paymentPlan: nextPlanWithInvoices,
          });
          const invoiceOk = await persistFinancials(invoiceFinancials, job, autoAuditEntries);
          if (invoiceOk) {
            setAuditLog((prev) => [...prev, ...autoAuditEntries]);
            await loadDbFinancialArtifacts();
            toast.success(`Financing approved — ${approvedLines.length} invoice(s) created and sent`);
            return;
          }
        }
      }
    }

    toast.success(`Financing ${nextStatus}`);
  };

  const requestPaymentModeChange = (mode: PaymentPlanType) => {
    if (mode === currentPaymentMode) return;
    setPendingPaymentMode(mode);
  };

  const confirmPaymentModeChange = async () => {
    if (!pendingPaymentMode) return;
    const mode = pendingPaymentMode;
    setPendingPaymentMode(null);
    await handlePaymentModeChange(mode);
  };

  const resetStructureLineForm = () => {
    setEditingStructureLineId(null);
    setStructureLineKind("progress");
    setStructureLineMethod(currentPaymentMode === "financed" ? "financed" : "cash");
    setStructureLineStatus("pending");
    setStructureLineDueAt("pre_install");
    setStructureLineAmount("");
    setStructureLineAmountMode("fixed");
    setStructureLinePercentage("");
  };

  const openAddStructureLine = () => {
    resetStructureLineForm();
    if (displayOutstandingBalance > 0 && adjustedContractValue > 0) {
      setStructureLineAmountMode("percentage");
      setStructureLinePercentage(String(roundCurrency((displayOutstandingBalance / adjustedContractValue) * 100)));
      setStructureLineAmount(String(roundCurrency(displayOutstandingBalance)));
    } else {
      setStructureLineAmountMode("fixed");
      setStructureLineAmount("");
    }
    setStructureLineOpen(true);
  };

  const openEditStructureLine = (line: PaymentLineItem) => {
    if (line.status === "paid" || line.status === "waived") {
      toast.info(`${line.kind[0].toUpperCase()}${line.kind.slice(1)} payment line is locked because it is ${line.status}.`);
      return;
    }
    setEditingStructureLineId(line.id);
    setStructureLineKind(line.kind);
    setStructureLineMethod(line.method);
    setStructureLineStatus(line.status);
    setStructureLineDueAt(line.dueAt ?? (line.kind === "final" ? "post_install" : "pre_install"));
    setStructureLineAmountMode("fixed");
    setStructureLinePercentage("");
    setStructureLineAmount(String(roundCurrency(Number(line.amount || 0))));
    setStructureLineOpen(true);
  };

  const persistPaymentStructure = async (
    lineItems: PaymentLineItem[],
    auditDetail: string,
    previousJob: Job = job,
  ) => {
    const basePlan = paymentPlan ?? buildPaymentPlan(currentPaymentMode ?? "cash", adjustedContractValue);
    const reconciledLineItems = reconcilePaymentLineStatuses(lineItems, mergedInvoices);
    const depositAmount = reconciledLineItems
      .filter((line) => line.kind === "deposit" && line.status !== "waived")
      .reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const nextPaymentPlan = {
      ...basePlan,
      requiresDeposit: depositAmount > 0,
      depositAmount: roundCurrency(depositAmount),
      lineItems: reconciledLineItems,
    };
    const nextFinancials = updateJobFinancials({ paymentPlan: nextPaymentPlan });
    const newAuditEntry = createAuditEntry("plan_generated", auditDetail);
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return false;
    setAuditLog((prev) => [...prev, newAuditEntry]);
    return true;
  };

  const getDefaultFinalMethod = (): PaymentLineItemMethod => {
    if (currentPaymentMode === "financed" || currentPaymentMode === "mixed") return "financed";
    if (currentPaymentMode === "card") return "card";
    return "cash";
  };

  const rebalanceFinalPaymentLine = (lineItems: PaymentLineItem[]): PaymentLineItem[] => {
    const nonFinalTotal = lineItems
      .filter((line) => line.kind !== "final" && line.status !== "waived")
      .reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const finalAmount = Math.max(0, roundCurrency(adjustedContractValue - nonFinalTotal));
    const finalIndex = lineItems.findIndex((line) => line.kind === "final");

    if (finalIndex >= 0) {
      return lineItems.map((line, index) => (
        index === finalIndex ? { ...line, amount: finalAmount } : line
      ));
    }

    if (finalAmount <= 0) return lineItems;

    return [
      ...lineItems,
      {
        id: crypto.randomUUID(),
        kind: "final",
        method: getDefaultFinalMethod(),
        amount: finalAmount,
        status: currentPaymentMode === "financed" ? "approved" : "pending",
        dueAt: currentPaymentMode === "financed" || currentPaymentMode === "mixed" ? "pre_install" : "post_install",
      },
    ];
  };

  // Maps structure line payment method to the DB payment method enum.
  const toDbPaymentMethod = (method: PaymentLineItemMethod): DbPaymentMethod => {
    if (method === "card") return "credit_card";
    if (method === "financed") return "bank_transfer";
    return "cash";
  };

  const handleSaveStructureLine = async () => {
    const percentageMode = structureLineAmountMode === "percentage";
    const pct = Number(structureLinePercentage);
    if (percentageMode && (!Number.isFinite(pct) || pct <= 0 || pct > 100)) {
      toast.error("Enter a payment line percentage between 1 and 100.");
      return;
    }
    const amount = percentageMode ? computedStructureLineAmount : Number(structureLineAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Enter a valid payment line amount.");
      return;
    }

    const previousJob = job;
    const currentLines = paymentPlan?.lineItems ?? [];
    const nextLine: PaymentLineItem = {
      id: editingStructureLineId ?? crypto.randomUUID(),
      kind: structureLineKind,
      method: structureLineMethod,
      amount: roundCurrency(amount),
      status: structureLineStatus,
      dueAt: structureLineDueAt,
    };
    const rawNextLines = editingStructureLineId
      ? currentLines.map((line) => line.id === editingStructureLineId ? nextLine : line)
      : [...currentLines, nextLine];
    const shouldRebalanceFinal = nextLine.kind !== "final";
    const nextLines = shouldRebalanceFinal ? rebalanceFinalPaymentLine(rawNextLines) : rawNextLines;
    const ok = await persistPaymentStructure(
      nextLines,
      `${editingStructureLineId ? "Updated" : "Added"} ${structureLineKind} payment structure line: $${formatMoney(nextLine.amount)}${percentageMode ? ` (${structureLinePercentage}% of net contract)` : ""}${shouldRebalanceFinal ? " · final payment auto-adjusted" : ""}`,
      previousJob,
    );
    if (!ok) return;

    // When status is approved or paid, auto-create + send invoice (and record payment for paid).
    if (nextLine.status === "approved" || nextLine.status === "paid") {
      const needsPayment = nextLine.status === "paid";
      const lineJob = job; // capture current job after structure save

      // Reuse existing invoice of same kind if available; create a new one otherwise.
      const existingInvoice = mergedInvoices.find((inv) => inv.type === nextLine.kind && inv.status !== "paid");
      let invoiceId: string;
      let invoiceAmount: number;
      let wasCreated = false;
      let newInvoiceNumber: string | undefined;

      if (existingInvoice && isUuid(existingInvoice.id)) {
        invoiceId = existingInvoice.id;
        invoiceAmount = existingInvoice.amount;
      } else {
        setSaving(true);
        const { invoice: created, error: createError } = await AccountingService.createInvoiceForJob({
          jobId: lineJob.id,
          type: nextLine.kind,
          amount: nextLine.amount,
          status: "draft",
          issuedAt: new Date().toISOString(),
          description: `${nextLine.kind[0].toUpperCase()}${nextLine.kind.slice(1)} payment`,
        });
        setSaving(false);
        if (createError || !created) {
          toast.error(getErrorMessage(createError, "Failed to create invoice"));
          setStructureLineOpen(false);
          resetStructureLineForm();
          return;
        }
        invoiceId = created.id;
        invoiceAmount = nextLine.amount;
        wasCreated = true;
        newInvoiceNumber = created.invoice_number ?? undefined;
      }

      // Auto-send: update invoice status to "issued".
      setSaving(true);
      const { error: sendError } = await AccountingService.updateInvoiceStatus(invoiceId, "issued");
      setSaving(false);
      if (sendError) {
        toast.error("Failed to send invoice");
        setStructureLineOpen(false);
        resetStructureLineForm();
        return;
      }

      let updatedInvoices = mergedInvoices.some((inv) => inv.id === invoiceId)
        ? mergedInvoices.map((inv) => inv.id === invoiceId ? { ...inv, status: "sent" as const } : inv)
        : [...mergedInvoices, { id: invoiceId, type: nextLine.kind, amount: invoiceAmount, status: "sent" as const, issuedAt: new Date().toISOString() }];
      let updatedPayments = [...payments];
      const auditEntries: InvoiceAuditEntry[] = [
        ...(wasCreated ? [createAuditEntry("invoice_created", `${nextLine.kind} invoice auto-created: $${formatMoney(invoiceAmount)}`, { invoiceId })] : []),
        createAuditEntry("invoice_sent", `${nextLine.kind} invoice auto-sent: $${formatMoney(invoiceAmount)}`, { invoiceId }),
      ];

      if (needsPayment) {
        const paidAt = new Date().toISOString();
        setSaving(true);
        const { payment, error: payError } = await AccountingService.recordPayment({
          invoiceId,
          amount: invoiceAmount,
          method: toDbPaymentMethod(nextLine.method),
          notes: "Auto-recorded on Paid status",
          paidAt,
        });
        setSaving(false);
        if (payError || !payment) {
          toast.error(getErrorMessage(payError, "Failed to record payment"));
          setStructureLineOpen(false);
          resetStructureLineForm();
          return;
        }
        updatedInvoices = updatedInvoices.map((inv) =>
          inv.id === invoiceId ? { ...inv, status: "paid" as const, paidAt } : inv
        );
        updatedPayments = [
          ...payments,
          { id: payment.id, invoiceId: payment.invoice_id, amount: Number(payment.amount), method: payment.method, timestamp: payment.paid_at },
        ];
        auditEntries.push(
          createAuditEntry("payment_recorded", `$${formatMoney(invoiceAmount)} auto-recorded`, { invoiceId, paymentId: payment.id }),
        );
      }

      // Use nextLines (the lines we just persisted with status "paid") as the source of
      // truth — NOT stale paymentPlan.lineItems which pre-dates the user's edit.
      const basePlanForInvoice = paymentPlan ?? buildPaymentPlan(currentPaymentMode ?? "cash", adjustedContractValue);
      const nextPaymentPlan = {
        ...basePlanForInvoice,
        lineItems: reconcilePaymentLineStatuses(nextLines, updatedInvoices),
      };
      const nextFinancials = updateJobFinancials({
        invoices: updatedInvoices,
        payments: updatedPayments,
        paymentPlan: nextPaymentPlan,
      });
      const invoiceOk = await persistFinancials(nextFinancials, lineJob, auditEntries);
      await loadDbFinancialArtifacts();
      if (invoiceOk) {
        setAuditLog((prev) => [...prev, ...auditEntries]);
        const numLabel = newInvoiceNumber ? ` · ${newInvoiceNumber}` : "";
        toast.success(needsPayment
          ? `Invoice created, sent, and payment recorded${numLabel}`
          : `Invoice created and sent${numLabel}`
        );
      }
      setStructureLineOpen(false);
      resetStructureLineForm();
      return;
    }

    setStructureLineOpen(false);
    resetStructureLineForm();
    toast.success("Payment structure updated");
  };

  const handleRemoveStructureLine = async () => {
    if (!editingStructureLineId) return;
    const line = paymentPlan?.lineItems.find((item) => item.id === editingStructureLineId);
    const rawNextLines = (paymentPlan?.lineItems ?? []).filter((item) => item.id !== editingStructureLineId);
    const shouldRebalanceFinal = line?.kind !== "final";
    const nextLines = shouldRebalanceFinal ? rebalanceFinalPaymentLine(rawNextLines) : rawNextLines;
    const ok = await persistPaymentStructure(
      nextLines,
      `Removed ${line?.kind ?? "payment"} payment structure line${line ? `: $${formatMoney(line.amount)}` : ""}${shouldRebalanceFinal ? " · final payment auto-adjusted" : ""}`,
    );
    if (!ok) return;
    setStructureLineOpen(false);
    resetStructureLineForm();
    toast.success("Payment structure line removed");
  };

  const openInvoiceFromStructureLine = (line: PaymentLineItem) => {
    openCreateInvoiceForm(line.kind, Number(line.amount || 0));
    setDepositAmountMode("fixed");
    setInvoiceNotes(`Generated from ${line.kind} payment structure line (${line.method}, ${PAYMENT_DUE_LABELS[line.dueAt ?? "pre_install"]}).`);
  };

  const persistFinancials = async (
    nextFinancials: Job["financials"],
    previousJob?: Job,
    auditEntries: InvoiceAuditEntry[] = [],
  ) => {
    const nextActivityHistory = [
      ...(job.activityHistory ?? []),
      ...auditEntries.map(toActivityEntry),
    ];
    setSaving(true);
    const { data, error } = await JobService.update(job.id, {
      financials: nextFinancials,
      activityHistory: nextActivityHistory,
    });
    setSaving(false);
    if (error || !data) {
      console.error("[JobFinancialsPanel] Failed to persist financials:", error);
      if (previousJob) onJobUpdate(previousJob);
      toast.error("Failed to save financial changes");
      return false;
    }
    onJobUpdate(data);
    // Re-sync DB invoices/payments so mergedInvoices reflects the latest state
    void loadDbFinancialArtifacts();
    return true;
  };

  const resetInvoiceForm = () => {
    setInvoiceType("deposit");
    setInvoiceAmount("");
    setDepositAmountMode("percentage");
    setDepositPercentage("50");
    setInvoiceIssuedDate(todayInputDate());
    setInvoiceDueDate("");
    setInvoiceNotes("");
  };

  const openCreateInvoiceForm = (type: InvoiceType, amount?: number) => {
    setInvoiceType(type);
    setDepositAmountMode(type === "deposit" ? "percentage" : "fixed");
    setDepositPercentage("50");
    setInvoiceAmount(amount != null && amount > 0 ? String(Math.round(amount * 100) / 100) : "");
    setInvoiceIssuedDate(todayInputDate());
    setInvoiceDueDate("");
    setInvoiceNotes("");
    setInvoiceFormOpen(true);
  };

  const handleInvoiceTypeChange = (value: InvoiceType) => {
    setInvoiceType(value);
    if (value === "deposit") {
      setDepositAmountMode("percentage");
      setDepositPercentage("50");
      setInvoiceAmount(String(roundCurrency(adjustedContractValue * 0.5)));
    } else {
      setDepositAmountMode("fixed");
    }
  };

  const handleCreateInvoice = async () => {
    const percentageMode = invoiceType === "deposit" && depositAmountMode === "percentage";
    const pct = Number(depositPercentage);
    if (percentageMode && (!Number.isFinite(pct) || pct <= 0 || pct > 100)) {
      toast.error("Enter a deposit percentage between 1 and 100.");
      return;
    }
    const amount = percentageMode ? computedDepositAmount : Number(invoiceAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter an invoice amount greater than zero.");
      return;
    }

    const previousJob = job;
    setSaving(true);
    const { invoice: created, error } = await AccountingService.createInvoiceForJob({
      jobId: job.id,
      type: invoiceType,
      amount,
      status: "draft",
      issuedAt: dateInputToIso(invoiceIssuedDate),
      dueDate: invoiceDueDate || null,
      notes: invoiceNotes || null,
      description: percentageMode
        ? `${job.projectType || "Project"} ${depositPercentage}% deposit invoice`
        : `${job.projectType || "Project"} ${invoiceType} invoice`,
    });
    setSaving(false);

    if (error || !created) {
      console.error("[JobFinancialsPanel] Failed to create invoice:", error);
      toast.error(getErrorMessage(error, "Failed to create invoice"));
      return;
    }

    const uiInvoice = toUiInvoiceFromDb(created, invoiceType);
    const updatedInvoices = [...mergedInvoices, uiInvoice];
    const nextFinancials = updateJobFinancials({ invoices: updatedInvoices });
    const issuedAt = dateInputToIso(invoiceIssuedDate);
    const newAuditEntry = createAuditEntry(
      "invoice_created",
      `${invoiceType[0].toUpperCase()}${invoiceType.slice(1)} invoice created: $${formatMoney(amount)}${percentageMode ? ` (${depositPercentage}% deposit)` : ""}${issuedAt ? ` · issued ${new Date(issuedAt).toLocaleDateString()}` : ""}`,
      { invoiceId: uiInvoice.id },
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    await loadDbFinancialArtifacts();
    setAuditLog((prev) => [...prev, newAuditEntry]);
    setInvoiceFormOpen(false);
    resetInvoiceForm();
    const numLabel = created.invoice_number ? ` · ${created.invoice_number}` : "";
    toast.success(`Invoice created${numLabel}`);
  };

  const canEditInvoiceAmount = (invoice: Invoice): boolean => {
    if (invoice.status === "paid") return false;
    const dbRow = dbInvoiceById.get(invoice.id);
    if (dbRow && Number(dbRow.amount_paid || 0) > 0) return false;
    if (dbPayments.some((payment) => payment.invoice_id === invoice.id && Number(payment.amount || 0) > 0)) return false;
    return invoice.status === "draft" || invoice.status === "sent";
  };

  const openEditInvoiceAmount = (invoice: Invoice) => {
    if (!canEditInvoiceAmount(invoice)) {
      toast.info("Invoice amount is locked after payment is received.");
      return;
    }
    setEditingInvoice(invoice);
    setEditInvoiceAmount(String(invoice.amount));
  };

  const handleSaveInvoiceAmount = async () => {
    if (!editingInvoice) return;
    const amount = Number(editInvoiceAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter an invoice amount greater than zero.");
      return;
    }

    const previousJob = job;
    let updatedInvoice: Invoice = { ...editingInvoice, amount };

    if (isUuid(editingInvoice.id)) {
      setSaving(true);
      const { invoice: dbInvoice, error } = await AccountingService.updateInvoiceAmount(editingInvoice.id, amount);
      setSaving(false);
      if (error || !dbInvoice) {
        console.error("[JobFinancialsPanel] Failed to edit invoice amount:", error);
        toast.error(getErrorMessage(error, "Failed to edit invoice amount"));
        return;
      }
      updatedInvoice = toUiInvoiceFromDb(dbInvoice, editingInvoice.type);
    }

    const updatedInvoices = mergedInvoices.map((invoice) => (
      invoice.id === editingInvoice.id ? updatedInvoice : invoice
    ));
    const nextFinancials = updateJobFinancials({ invoices: updatedInvoices });
    const newAuditEntry = createAuditEntry(
      "invoice_created",
      `${editingInvoice.type[0].toUpperCase()}${editingInvoice.type.slice(1)} invoice amount updated to $${formatMoney(amount)}`,
      { invoiceId: editingInvoice.id },
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    await loadDbFinancialArtifacts();
    setAuditLog((prev) => [...prev, newAuditEntry]);
    setEditingInvoice(null);
    setEditInvoiceAmount("");
    toast.success("Invoice amount updated");
  };

  // Intentionally do not backfill subcontractor payout from job.payoutAmount.
  // payoutAmount is legacy/ambiguous and can mirror customer contract value,
  // which would incorrectly inflate install cost and distort margin.

  const handleSendInvoice = async (invoice: Invoice) => {
    if (isUuid(invoice.id)) {
      const { error } = await AccountingService.updateInvoiceStatus(invoice.id, "issued");
      if (error) {
        console.error("[JobFinancialsPanel] Failed to update DB invoice status:", error);
        toast.error("Failed to send invoice");
        return;
      }
    }
    const previousJob = job;
    const updated = mergedInvoices.map(i => i.id === invoice.id ? { ...i, status: "sent" as const } : i);
    const nextFinancials = updateJobFinancials({ invoices: updated });
     const newAuditEntry = createAuditEntry("invoice_sent", `Invoice ${displayIdentity(invoice.id, "[ID]")} sent to customer`, { invoiceId: invoice.id });
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;
    setAuditLog(prev => [...prev, newAuditEntry]);
    toast.success("Invoice sent");
  };

  const handleRecordPayment = (invoice: Invoice) => {
    const outstanding = getOutstandingBalanceForInvoice(invoice);
    if (outstanding <= 0) {
      toast.info("Invoice is already fully paid.");
      return;
    }
    setPaymentInvoice(invoice);
    setPaymentAmount(String(Math.round(outstanding * 100) / 100));
    setPaymentMethod("credit_card");
    setPaymentReceivedDate(todayInputDate());
    setPaymentReference("");
    setPaymentNotes("");
  };

  const handleSubmitPayment = async () => {
    if (!paymentInvoice) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a payment amount greater than zero.");
      return;
    }

    const outstanding = getOutstandingBalanceForInvoice(paymentInvoice);
    if (amount > outstanding) {
      toast.error(`Payment cannot exceed the invoice balance of $${formatMoney(outstanding)}.`);
      return;
    }

    const previousJob = job;
    const paidAt = dateInputToIso(paymentReceivedDate) ?? new Date().toISOString();

    if (isUuid(paymentInvoice.id)) {
      setSaving(true);
      const { payment, invoice: updatedInvoice, error } = await AccountingService.recordPayment({
        invoiceId: paymentInvoice.id,
        amount,
        method: paymentMethod,
        reference: paymentReference || null,
        notes: paymentNotes || "Recorded from Job Financials panel",
        paidAt,
      });
      setSaving(false);

      if (error || !payment) {
        console.error("[JobFinancialsPanel] Failed to record DB payment:", error);
        const message = getErrorMessage(error, "Payment failed");
        if (message.toLowerCase().includes("already fully paid")) toast.info(message);
        else toast.error(message);
        return;
      }

      const updatedInvoices = mergedInvoices.map((invoice) => {
        if (invoice.id !== paymentInvoice.id) return invoice;
        return {
          ...invoice,
          status: mapDbInvoiceStatusToUi(updatedInvoice?.status ?? invoice.status),
          paidAt: updatedInvoice?.paid_at ?? (Number(updatedInvoice?.balance_due ?? outstanding - amount) <= 0 ? paidAt : invoice.paidAt),
          paymentMethod: paymentMethod === "credit_card" || paymentMethod === "debit_card" || paymentMethod === "online" ? "card" as const : "ACH" as const,
        };
      });
      const updatedPayments = [
        ...payments,
        {
          id: payment.id,
          invoiceId: payment.invoice_id,
          amount: Number(payment.amount || 0),
          method: payment.method,
          timestamp: payment.paid_at,
        },
      ];
      const nextPaymentPlan = paymentPlan
        ? { ...paymentPlan, lineItems: reconcilePaymentLineStatuses(paymentPlan.lineItems, updatedInvoices) }
        : undefined;
      const nextFinancials = updateJobFinancials({
        invoices: updatedInvoices,
        payments: updatedPayments,
        ...(nextPaymentPlan ? { paymentPlan: nextPaymentPlan } : {}),
      });
      const newAuditEntry = createAuditEntry(
        "payment_recorded",
        `$${Number(payment.amount || 0).toLocaleString()} received on ${new Date(paidAt).toLocaleDateString()} for ${displayIdentity(paymentInvoice.id, "[ID]")}`,
        { invoiceId: paymentInvoice.id, paymentId: payment.id },
      );
      const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
      if (!ok) return;

      await loadDbFinancialArtifacts();
      setAuditLog((prev) => [...prev, newAuditEntry]);
      setPaymentInvoice(null);
      toast.success("Payment recorded");
      return;
    }

    const localPaymentId = `PAY-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const remainingAfterPayment = Math.max(0, outstanding - amount);
    const updatedInvoices = mergedInvoices.map((invoice) => {
      if (invoice.id !== paymentInvoice.id) return invoice;
      return {
        ...invoice,
        status: remainingAfterPayment <= 0 ? "paid" as const : "sent" as const,
        paidAt: remainingAfterPayment <= 0 ? paidAt : invoice.paidAt,
      };
    });
    const updatedPayments = [
      ...payments,
      {
        id: localPaymentId,
        invoiceId: paymentInvoice.id,
        amount,
        method: paymentMethod,
        timestamp: paidAt,
      },
    ];
    const nextPaymentPlan = paymentPlan
      ? { ...paymentPlan, lineItems: reconcilePaymentLineStatuses(paymentPlan.lineItems, updatedInvoices) }
      : undefined;
    const nextFinancials = updateJobFinancials({
      invoices: updatedInvoices,
      payments: updatedPayments,
      ...(nextPaymentPlan ? { paymentPlan: nextPaymentPlan } : {}),
    });
    const newAuditEntry = createAuditEntry(
      "payment_recorded",
      `$${amount.toLocaleString()} received on ${new Date(paidAt).toLocaleDateString()} for ${paymentInvoice.id}`,
      { invoiceId: paymentInvoice.id, paymentId: localPaymentId },
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    setAuditLog((prev) => [...prev, newAuditEntry]);
    setPaymentInvoice(null);
    toast.success("Payment recorded");
  };

  const handleRecordRefund = async () => {
    const amount = Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a refund amount greater than zero.");
      return;
    }
    if (amount > refundableBalance) {
      toast.error(`Refund cannot exceed the refundable balance of $${formatMoney(refundableBalance)}.`);
      return;
    }

    const previousJob = job;
    const settledAt = dateInputToIso(refundSettlementDate) ?? new Date().toISOString();
    const refundPaymentId = `REF-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const refundPayment = {
      id: refundPaymentId,
      invoiceId: "refund",
      amount: -amount,
      method: "refund",
      timestamp: settledAt,
    };
    const nextFinancials = updateJobFinancials({ payments: [...payments, refundPayment] });
    const newAuditEntry = createAuditEntry(
      "refund_recorded",
      `$${amount.toLocaleString()} refund issued on ${new Date(settledAt).toLocaleDateString()}${refundNotes.trim() ? ` — ${refundNotes.trim()}` : ""}`,
      { paymentId: refundPaymentId },
    );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    setAuditLog((prev) => [...prev, newAuditEntry]);
    setRefundFormOpen(false);
    setRefundAmount("");
    setRefundSettlementDate(todayInputDate());
    setRefundNotes("");
    toast.success("Refund recorded");
  };

  const handleVoidInvoice = async (invoice: Invoice) => {
    if (!isUuid(invoice.id)) {
      toast.info("Only DB-backed invoices can be voided.");
      return;
    }

    const dbRow = dbInvoiceById.get(invoice.id);
    const paidFromInvoice = Number(dbRow?.amount_paid ?? 0);
    const hasPaymentRows = dbPayments.some((payment) => payment.invoice_id === invoice.id && Number(payment.amount || 0) > 0);
    if (paidFromInvoice > 0 || hasPaymentRows) {
      toast.error("Cannot void an invoice with recorded payments.");
      return;
    }

    const { invoice: updatedDbInvoice, error } = await AccountingService.updateInvoiceStatus(invoice.id, "void");
    if (error) {
      console.error("[JobFinancialsPanel] Failed to void invoice:", error);
      toast.error("Failed to void invoice.");
      return;
    }

    const previousJob = job;
    const updatedInvoices = mergedInvoices.filter((inv) => inv.id !== invoice.id);
    const nextPaymentPlan = paymentPlan
      ? { ...paymentPlan, lineItems: reconcilePaymentLineStatuses(paymentPlan.lineItems, updatedInvoices) }
      : undefined;
    const nextFinancials = updateJobFinancials({
      invoices: updatedInvoices,
      ...(nextPaymentPlan ? { paymentPlan: nextPaymentPlan } : {}),
    });
     const newAuditEntry = createAuditEntry(
       "invoice_voided",
       `Invoice ${displayIdentity(invoice.id, "[ID]")} voided${updatedDbInvoice?.invoice_number ? ` (${updatedDbInvoice.invoice_number})` : ""}`,
       { invoiceId: invoice.id },
     );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    await loadDbFinancialArtifacts();
    setAuditLog((prev) => [...prev, newAuditEntry]);
    toast.success("Invoice voided");
  };

  const handleReverseAndVoidInvoice = async (invoice: Invoice) => {
    if (!isUuid(invoice.id)) {
      toast.info("Only DB-backed invoices can be reversed and voided.");
      return;
    }

    const { invoice: updatedDbInvoice, removedPayments, error } = await AccountingService.reversePaymentsAndVoidInvoice(invoice.id);
    if (error) {
      console.error("[JobFinancialsPanel] Failed to reverse and void invoice:", error);
      toast.error("Failed to reverse payment and void invoice.");
      return;
    }

    const previousJob = job;
    const updatedInvoices = mergedInvoices.filter((inv) => inv.id !== invoice.id);
    const updatedPayments = payments.filter((payment) => payment.invoiceId !== invoice.id);
    const nextPaymentPlan = paymentPlan
      ? { ...paymentPlan, lineItems: reconcilePaymentLineStatuses(paymentPlan.lineItems, updatedInvoices) }
      : undefined;
    const nextFinancials = updateJobFinancials({
      invoices: updatedInvoices,
      payments: updatedPayments,
      ...(nextPaymentPlan ? { paymentPlan: nextPaymentPlan } : {}),
    });
     const newAuditEntry = createAuditEntry(
       "invoice_voided",
       `Invoice ${displayIdentity(invoice.id, "[ID]")} reversed${removedPayments > 0 ? ` (${removedPayments} payment${removedPayments > 1 ? "s" : ""})` : ""} and voided${updatedDbInvoice?.invoice_number ? ` (${updatedDbInvoice.invoice_number})` : ""}`,
       { invoiceId: invoice.id },
     );
    const ok = await persistFinancials(nextFinancials, previousJob, [newAuditEntry]);
    if (!ok) return;

    await loadDbFinancialArtifacts();
    setAuditLog((prev) => [...prev, newAuditEntry]);
    toast.success("Payment reversed and invoice voided");
  };

  return (
    <div className="space-y-4">
      {/* ── Settlement Policy Banner ── */}
      {policy.restriction && (
        <div className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 ${
          policy.severity === "block" ? "border-destructive/30 bg-destructive/5" :
          policy.severity === "warning" ? "border-warning/30 bg-warning/5" :
          "border-muted bg-muted/30"
        }`}>
          {policy.severity === "block" ? (
            <Ban className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
          ) : policy.severity === "warning" ? (
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
          ) : (
            <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          )}
          <div className="flex-1">
            <p className={`text-sm font-semibold ${
              policy.severity === "block" ? "text-destructive" :
              policy.severity === "warning" ? "text-warning" :
              "text-muted-foreground"
            }`}>
              {SETTLEMENT_LABELS[policy.status]?.label ?? "Settlement Status"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{policy.restriction}</p>
          </div>
        </div>
      )}

      {/* ── Payment Mode ── */}
      <div className="rounded-xl border border-border/60 bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            {(() => {
              const Icon = currentPaymentMode ? METHOD_ICONS[currentPaymentMode] ?? DollarSign : DollarSign;
              return <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />;
            })()}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Payment Mode</p>
              <p className="text-sm font-semibold text-foreground">
                {currentPaymentMode ? PAYMENT_MODE_LABELS[currentPaymentMode] : "Not set"}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {paymentPlan
                  ? `Deposit ${paymentPlan.requiresDeposit ? `$${formatMoney(paymentPlan.depositAmount)}` : "not required"} · ${paymentPlan.lineItems.length} payment line${paymentPlan.lineItems.length === 1 ? "" : "s"}`
                  : "Set whether this job is cash, financed, card, or combined."}
              </p>
            </div>
          </div>
          {canManage ? (
            <Select
              value={currentPaymentMode ?? ""}
              onValueChange={(value) => requestPaymentModeChange(value as PaymentPlanType)}
              disabled={saving}
            >
              <SelectTrigger className="w-full sm:w-52">
                <SelectValue placeholder="Set payment mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash / Check</SelectItem>
                <SelectItem value="financed">Financing</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="mixed">Combined</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="w-fit text-[10px]">
              {currentPaymentMode ? PAYMENT_MODE_LABELS[currentPaymentMode] : "Missing"}
            </Badge>
          )}
        </div>
      </div>

      {/* ── Financial Clearance Status ── */}
      <div className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 ${
        clearance.status === "blocked" ? "border-destructive/30 bg-destructive/5" :
        clearance.status === "pending" ? "border-warning/30 bg-warning/5" :
        "border-success/30 bg-success/5"
      }`}>
        <ShieldAlert className={`h-4 w-4 mt-0.5 shrink-0 ${getClearanceColor(clearance.status)}`} />
        <div className="flex-1">
          <p className={`text-sm font-semibold ${getClearanceColor(clearance.status)}`}>
            {getClearanceLabel(clearance.status)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{clearance.reason}</p>
          {clearance.details.length > 1 && (
            <div className="mt-2 space-y-1">
              {clearance.details.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  {d.met ? (
                    <CheckCircle className="h-3 w-3 text-success shrink-0" />
                  ) : (
                    <XCircle className="h-3 w-3 text-destructive shrink-0" />
                  )}
                  <span className="text-[10px] text-muted-foreground">{d.explanation}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Payment Structure ── */}
      {paymentPlan && (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {(() => { const Icon = METHOD_ICONS[paymentPlan.type] ?? DollarSign; return <Icon className="h-4 w-4 text-muted-foreground" />; })()}
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Payment Structure</span>
            <Badge variant="outline" className="ml-auto text-[10px] capitalize">{paymentPlan.type}</Badge>
            {canManage && (
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={openAddStructureLine} disabled={saving}>
                Add line
              </Button>
            )}
          </div>

          {hasFinancedStructure && (
            <div className={`flex flex-col gap-3 mb-3 rounded-lg px-3 py-2 sm:flex-row sm:items-center ${
              financing?.financingStatus === "approved" ? "bg-success/5 border border-success/20" :
              financing?.financingStatus === "declined" ? "bg-destructive/5 border border-destructive/20" :
              "bg-warning/5 border border-warning/20"
            }`}>
              <div className="flex flex-1 items-start gap-2">
                <Landmark className={`mt-0.5 h-3.5 w-3.5 ${
                  financing?.financingStatus === "approved" ? "text-success" :
                  financing?.financingStatus === "declined" ? "text-destructive" : "text-warning"
                }`} />
                <div>
                  <p className="text-xs font-semibold">
                    Financing {financing?.financingStatus && financing.financingStatus !== "not_applicable" ? financing.financingStatus : "pending"}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {financing?.financingProvider ? `${FINANCING_PROVIDER_LABELS[financing.financingProvider] ?? financing.financingProvider} · ` : ""}
                    Amount: ${formatMoney(financing?.financingApprovedAmount ?? financedStructureTotal)}
                    {financing?.customerDownPaymentAmount ? ` · Down: $${formatMoney(financing.customerDownPaymentAmount)}` : ""}
                  </p>
                </div>
              </div>
              {canManage && (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={financing?.financingProvider ?? "unassigned"}
                    onValueChange={(value) => void handleFinancingDetailsChange({ financingProvider: value })}
                    disabled={saving}
                  >
                    <SelectTrigger className="h-8 w-36 text-xs">
                      <SelectValue placeholder="Provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Provider TBD</SelectItem>
                      <SelectItem value="synchrony">Synchrony</SelectItem>
                      <SelectItem value="greensky">GreenSky</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={financing?.financingStatus && financing.financingStatus !== "not_applicable" ? financing.financingStatus : "pending"}
                    onValueChange={(value) => void handleFinancingDetailsChange({ financingStatus: value as "pending" | "approved" | "declined" })}
                    disabled={saving}
                  >
                    <SelectTrigger className="h-8 w-32 text-xs">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="declined">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[10px]"
                    onClick={() => void handleFinancingDetailsChange({ financingStatus: "approved" })}
                    disabled={saving || financing?.financingStatus === "approved"}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[10px] text-destructive"
                    onClick={() => void handleFinancingDetailsChange({ financingStatus: "declined" })}
                    disabled={saving || financing?.financingStatus === "declined"}
                  >
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            {displayPaymentPlanLines.map(li => (
              <div key={li.id} className="flex flex-col gap-2 py-2 border-b border-border/30 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  {li.status === "paid" || li.status === "waived" ? (
                    <CheckCircle className="h-3 w-3 text-success" />
                  ) : (
                    <Clock className="h-3 w-3 text-warning" />
                  )}
                  <span className="text-xs font-medium capitalize">{li.kind}</span>
                  <Badge variant="outline" className="text-[9px] capitalize">{li.method}</Badge>
                  <Badge variant="outline" className="text-[9px]">{PAYMENT_DUE_LABELS[li.dueAt ?? "pre_install"]}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold">${formatMoney(li.amount)}</span>
                  <Badge variant="outline" className={`text-[9px] ${
                    li.status === "paid" ? "text-success" :
                    li.status === "waived" ? "text-muted-foreground" :
                    li.status === "approved" ? "text-primary" : "text-warning"
                  }`}>{li.status}</Badge>
                  {canManage && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px]" onClick={() => openEditStructureLine(li)} disabled={saving || li.status === "paid"}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Contract Summary ── */}
      <div className="rounded-xl border border-border/60 bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Contract Summary</span>
          <Badge variant="outline" className={`ml-auto text-[10px] ${paymentStatusCfg.color}`}>
            {paymentStatusCfg.label}
          </Badge>
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px] gap-1 px-2"
              onClick={() => {
                setContractValueInput(String(summary.contractValue ?? 0));
                setContractValueModalOpen(true);
              }}
            >
              Change value
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3 md:grid-cols-7">
          <div>
            <p className="text-[10px] text-muted-foreground">Contract</p>
            <p className="text-sm font-bold text-foreground">${formatMoney(summary.contractValue)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Credits</p>
            <p className={`text-sm font-bold ${appliedCreditTotal > 0 ? "text-primary" : "text-muted-foreground"}`}>
              ${formatMoney(appliedCreditTotal)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Net Contract</p>
            <p className="text-sm font-bold text-foreground">${formatMoney(adjustedContractValue)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Invoiced</p>
            <p className={`text-sm font-bold ${totalInvoiced > 0 ? "text-foreground" : "text-muted-foreground"}`}>
              ${formatMoney(totalInvoiced)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Paid</p>
            <p className="text-sm font-bold text-success">${formatMoney(displayTotalPaid)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Outstanding</p>
            <p className={`text-sm font-bold ${displayOutstandingBalance > 0 ? "text-warning" : "text-success"}`}>
              ${formatMoney(displayOutstandingBalance)}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Refundable</p>
            <p className={`text-sm font-bold ${refundableBalance > 0 ? "text-warning" : "text-muted-foreground"}`}>
              ${formatMoney(refundableBalance)}
            </p>
          </div>
        </div>

        {(appliedCreditTotal > 0 || pendingCreditTotal > 0) && (
          <p className="mb-3 text-[10px] text-muted-foreground">
            Credits reduce the customer balance once approved/offered/accepted/applied.
            {pendingCreditTotal > 0 ? ` Pending draft/proposed credits: $${formatMoney(pendingCreditTotal)}.` : ""}
          </p>
        )}

        <div className="flex items-center gap-2">
          <Progress value={paidPct} className="h-1.5 flex-1 [&>div]:bg-success" />
          <span className="text-[10px] font-semibold text-muted-foreground">{paidPct}%</span>
        </div>
      </div>

      {/* ── Refund Actions ── */}
      {canManage && refundableBalance > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="mr-auto">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Refund Actions</p>
              <p className="text-[11px] text-muted-foreground">Invoices are generated from payment structure lines above.</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-xs text-warning"
              onClick={() => {
                setRefundAmount(String(Math.round(refundableBalance * 100) / 100));
                setRefundSettlementDate(todayInputDate());
                setRefundNotes("");
                setRefundFormOpen(true);
              }}
              disabled={saving}
            >
              <Wallet className="h-3.5 w-3.5 mr-1.5" /> Record Refund
            </Button>
          </div>
        </div>
      )}

      {/* ── Invoice Timeline ── */}
      {mergedInvoices.length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Invoices</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{mergedInvoices.length} total</span>
          </div>
          <div className="space-y-2">
            {mergedInvoices.map(inv => {
              const cfg = STATUS_CONFIG[inv.status] ?? STATUS_CONFIG.draft;
              const StatusIcon = cfg.icon;
              return (
                <div key={inv.id} className="flex items-center gap-3 rounded-lg border border-border/40 px-3 py-2.5">
                  <div className={`rounded-full p-1.5 ${cfg.color}`}>
                    <StatusIcon className="h-3 w-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {inv.invoiceNumber && (
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">{inv.invoiceNumber}</span>
                      )}
                      <span className="text-xs font-semibold capitalize">{inv.type}</span>
                      <Badge variant="outline" className={`text-[9px] ${cfg.color}`}>{cfg.label}</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      ${formatMoney(inv.amount)}
                      {inv.dueDate && ` · Due ${new Date(inv.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    </p>
                  </div>
                  {canManage && inv.status === "draft" && policy.canSendInvoice && (
                    <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2" onClick={() => handleSendInvoice(inv)} disabled={saving}>
                      <Send className="h-3 w-3 mr-1" /> Send
                    </Button>
                  )}
                  {canManage && canEditInvoiceAmount(inv) && (
                    <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2" onClick={() => openEditInvoiceAmount(inv)} disabled={saving}>
                      <DollarSign className="h-3 w-3 mr-1" /> Edit Amount
                    </Button>
                  )}
                  {canManage && (inv.status === "sent" || inv.status === "overdue") && policy.canCollectPayment && getOutstandingBalanceForInvoice(inv) > 0 && (
                    <Button size="sm" variant="ghost" className="text-[10px] h-7 px-2" onClick={() => handleRecordPayment(inv)} disabled={saving}>
                      <CreditCard className="h-3 w-3 mr-1" /> Record Payment
                    </Button>
                  )}
                  {canManage && (inv.status === "sent" || inv.status === "overdue") && !policy.canCollectPayment && policy.restriction && (
                    <span className="text-[9px] text-muted-foreground italic">{policy.severity === "block" ? "Blocked" : "Deferred"}</span>
                  )}
                  {canManage && isUuid(inv.id) && inv.status !== "paid" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[10px] h-7 px-2 text-destructive"
                      onClick={() => handleVoidInvoice(inv)}
                      disabled={saving}
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Void
                    </Button>
                  )}
                  {canManage && isUuid(inv.id) && inv.status === "paid" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[10px] h-7 px-2 text-destructive"
                      onClick={() => handleReverseAndVoidInvoice(inv)}
                      disabled={saving}
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Reverse & Void
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Payment History ── */}
      {payments.length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Payment History</span>
          </div>
          <div className="space-y-1.5">
            {payments.map(p => {
              const isRefund = Number(p.amount || 0) < 0;
              return (
                <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0">
                  <div className="flex items-center gap-2">
                    {isRefund ? (
                      <Wallet className="h-3 w-3 text-warning" />
                    ) : (
                      <CheckCircle className="h-3 w-3 text-success" />
                    )}
                    <span className={`text-xs font-medium ${isRefund ? "text-warning" : ""}`}>
                      {isRefund ? "-" : ""}${formatMoney(Math.abs(Number(p.amount || 0)))}
                    </span>
                    <span className="text-[10px] text-muted-foreground capitalize">{isRefund ? "refund" : p.method}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(p.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Audit Log ── */}
      {visibleAuditLog.length > 0 && (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Financial Audit</span>
          </div>
          <div className="space-y-1">
            {visibleAuditLog.slice().reverse().map((entry) => (
              <div key={entry.id || `${entry.timestamp}-${entry.detail}`} className="flex items-start gap-2 py-1">
                <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 mt-1.5 shrink-0" />
                <div>
                  <p className="text-[11px] text-foreground">{entry.detail}</p>
                  <p className="text-[9px] text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={Boolean(pendingPaymentMode)} onOpenChange={(open) => {
        if (!open) setPendingPaymentMode(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Payment Mode Change</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2">
              <p className="text-sm font-semibold text-warning">This will rebuild the payment structure.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Existing payment structure lines may be replaced with the default structure for the selected mode.
                Existing invoices and recorded payments are not deleted.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Current</p>
                <p className="mt-1 font-semibold">{currentPaymentMode ? PAYMENT_MODE_LABELS[currentPaymentMode] : "Not set"}</p>
              </div>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">New</p>
                <p className="mt-1 font-semibold text-primary">{pendingPaymentMode ? PAYMENT_MODE_LABELS[pendingPaymentMode] : ""}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPendingPaymentMode(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={confirmPaymentModeChange} disabled={saving || !pendingPaymentMode}>
                {saving ? "Saving..." : "Confirm Change"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceFormOpen} onOpenChange={(open) => {
        setInvoiceFormOpen(open);
        if (!open) resetInvoiceForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Invoice Type</Label>
              <Select value={invoiceType} onValueChange={(value) => handleInvoiceTypeChange(value as InvoiceType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposit">Deposit</SelectItem>
                  <SelectItem value="progress">Progress</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {invoiceType === "deposit" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Deposit Amount Type</Label>
                <Select value={depositAmountMode} onValueChange={(value) => setDepositAmountMode(value as "fixed" | "percentage")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage of net contract</SelectItem>
                    <SelectItem value="fixed">Fixed amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {invoiceType === "deposit" && depositAmountMode === "percentage" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Deposit Percentage</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    step="0.01"
                    value={depositPercentage}
                    onChange={(event) => setDepositPercentage(event.target.value)}
                    placeholder="50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Calculated Amount</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm font-semibold">
                    ${formatMoney(computedDepositAmount)}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground sm:col-span-2">
                  Based on net contract ${formatMoney(adjustedContractValue)} after applied credits.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(event) => setInvoiceAmount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Issue Date</Label>
                <Input
                  type="date"
                  value={invoiceIssuedDate}
                  onChange={(event) => setInvoiceIssuedDate(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Due Date</Label>
                <Input
                  type="date"
                  value={invoiceDueDate}
                  onChange={(event) => setInvoiceDueDate(event.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={invoiceNotes}
                onChange={(event) => setInvoiceNotes(event.target.value)}
                placeholder="Optional invoice notes"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setInvoiceFormOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleCreateInvoice} disabled={saving}>{saving ? "Saving..." : "Create Invoice"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={structureLineOpen} onOpenChange={(open) => {
        setStructureLineOpen(open);
        if (!open) resetStructureLineForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingStructureLineId ? "Edit Payment Line" : "Add Payment Line"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Line Type</Label>
                <Select value={structureLineKind} onValueChange={(value) => setStructureLineKind(value as PaymentLineItemKind)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="progress">Progress</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Method</Label>
                <Select value={structureLineMethod} onValueChange={(value) => setStructureLineMethod(value as PaymentLineItemMethod)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="financed">Financed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount Type</Label>
              <Select value={structureLineAmountMode} onValueChange={(value) => setStructureLineAmountMode(value as "fixed" | "percentage")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage of net contract</SelectItem>
                  <SelectItem value="fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {structureLineAmountMode === "percentage" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Percentage</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    step="0.01"
                    value={structureLinePercentage}
                    onChange={(event) => setStructureLinePercentage(event.target.value)}
                    placeholder="50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Calculated Amount</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm font-semibold">
                    ${formatMoney(computedStructureLineAmount)}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground sm:col-span-2">
                  Based on net contract ${formatMoney(adjustedContractValue)} after applied credits.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={structureLineAmount}
                  onChange={(event) => setStructureLineAmount(event.target.value)}
                  placeholder="0.00"
                />
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Due Timing</Label>
                <Select value={structureLineDueAt} onValueChange={(value) => setStructureLineDueAt(value as PaymentDueAt)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_schedule">Before scheduling</SelectItem>
                    <SelectItem value="pre_install">Before install</SelectItem>
                    <SelectItem value="post_install">After install</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={structureLineStatus} onValueChange={(value) => setStructureLineStatus(value as PaymentLineItemStatus)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved — auto-sends invoice</SelectItem>
                    <SelectItem value="paid">Paid — auto-sends &amp; records payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {editingStructureLineId && (
                <Button variant="destructive" onClick={handleRemoveStructureLine} disabled={saving} className="mr-auto">
                  Remove
                </Button>
              )}
              <Button variant="outline" onClick={() => setStructureLineOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSaveStructureLine} disabled={saving}>{saving ? "Saving..." : "Save Line"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingInvoice)} onOpenChange={(open) => {
        if (!open) {
          setEditingInvoice(null);
          setEditInvoiceAmount("");
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Invoice Amount</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Amount can be changed until payment is received.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editInvoiceAmount}
                onChange={(event) => setEditInvoiceAmount(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingInvoice(null)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSaveInvoiceAmount} disabled={saving}>{saving ? "Saving..." : "Save Amount"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(paymentInvoice)} onOpenChange={(open) => {
        if (!open) setPaymentInvoice(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Invoice balance: ${formatMoney(paymentInvoice ? getOutstandingBalanceForInvoice(paymentInvoice) : 0)}
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount Received</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentAmount}
                onChange={(event) => setPaymentAmount(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Method</Label>
              <Select value={paymentMethod} onValueChange={(value) => setPaymentMethod(value as DbPaymentMethod)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Received / Verified Date</Label>
              <Input
                type="date"
                value={paymentReceivedDate}
                onChange={(event) => setPaymentReceivedDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reference</Label>
              <Input
                value={paymentReference}
                onChange={(event) => setPaymentReference(event.target.value)}
                placeholder="Check, ACH, processor, or memo number"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={paymentNotes}
                onChange={(event) => setPaymentNotes(event.target.value)}
                placeholder="Optional payment notes"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPaymentInvoice(null)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSubmitPayment} disabled={saving}>{saving ? "Saving..." : "Record Payment"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={refundFormOpen} onOpenChange={setRefundFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Refundable balance: ${formatMoney(refundableBalance)}. This records an auditable refund adjustment on the job.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Refund Amount</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={refundAmount}
                onChange={(event) => setRefundAmount(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Settlement Date</Label>
              <Input
                type="date"
                value={refundSettlementDate}
                onChange={(event) => setRefundSettlementDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={refundNotes}
                onChange={(event) => setRefundNotes(event.target.value)}
                placeholder="Reason, processor, or approval notes"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRefundFormOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleRecordRefund} disabled={saving}>{saving ? "Saving..." : "Record Refund"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── Change Contract Value Modal ── */}
      <Dialog open={contractValueModalOpen} onOpenChange={setContractValueModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Change Contract Value</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Current Value</Label>
              <p className="text-sm font-bold tabular-nums">${formatMoney(summary.contractValue)}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">New Contract Value</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={contractValueInput}
                  onChange={(e) => setContractValueInput(e.target.value)}
                  className="pl-6 tabular-nums"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setContractValueModalOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                disabled={saving}
                onClick={async () => {
                  const parsed = parseFloat(contractValueInput);
                  if (!Number.isFinite(parsed) || parsed <= 0) {
                    toast.error("Contract value must be greater than $0");
                    return;
                  }
                  const prev = summary.contractValue;
                  const previousJob = { ...job };
                  const nextFinancials = updateJobFinancials({ contractValue: parsed });
                  const auditEntry = createAuditEntry(
                    "plan_generated",
                    `Contract value updated: $${formatMoney(prev)} → $${formatMoney(parsed)}`,
                  );
                  const ok = await persistFinancials(nextFinancials, previousJob, [auditEntry]);
                  if (ok) {
                    setAuditLog((p) => [...p, auditEntry]);
                    setContractValueModalOpen(false);
                    toast.success("Contract value updated");
                  }
                }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
